import { useState, useRef, useEffect, useMemo } from "react"
import { ChevronDown, X, CheckCheck } from "lucide-react"
import taxonomyData from "../../assets/taxonomy.json"

const FF = "'Plus Jakarta Sans', 'Inter', sans-serif"
const CATEGORIES = taxonomyData.result
const COMPANIES_ENDPOINT = "https://jobscanner-e1dd4.ue.r.appspot.com/network/companies"
const MAX_RESULTS_PER_GROUP = 8

// Flatten taxonomy into a searchable list once
const ROLE_INDEX = (() => {
  const out = []
  for (const cat of CATEGORIES) {
    for (const sub of cat.second_category || []) {
      for (const item of sub.third_category || []) {
        out.push({
          id: item.id,
          title: item.display_name,
          group: sub.display_name,
          lc: item.display_name.toLowerCase(),
        })
      }
    }
  }
  return out
})()

// Module-level cache so the dropdown doesn't refetch on every open
let COMPANIES_CACHE = null

export default function RoleTaxonomyFilter({
  selected,
  onToggle,
  onClear,
  selectedCompanies = [],
  onCompanyToggle,
  onCompanyClear,
  C,
  renderTrigger,
  dropdownAlign = "left",
  wrapperStyle,
  query = "",
  onRequestClose,
  openProp,
}) {
  const [internalOpen, setInternalOpen] = useState(false)
  const open    = openProp !== undefined ? openProp : internalOpen
  const setOpen = (v) => {
    if (openProp !== undefined) {
      if (!v) onRequestClose?.()
    } else {
      setInternalOpen(typeof v === "function" ? v(internalOpen) : v)
    }
  }
  const [companies, setCompanies] = useState(COMPANIES_CACHE || [])
  const [loading, setLoading]     = useState(COMPANIES_CACHE == null)
  const ref = useRef(null)

  // Outside click → close
  useEffect(() => {
    if (!open) return
    const h = e => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false) } }
    document.addEventListener("mousedown", h)
    return () => document.removeEventListener("mousedown", h)
  }, [open])

  // Lazy-load companies on first open
  useEffect(() => {
    if (!open || COMPANIES_CACHE != null) return
    let cancelled = false
    setLoading(true)
    fetch(COMPANIES_ENDPOINT)
      .then(r => r.ok ? r.json() : { companies: [] })
      .then(data => {
        if (cancelled) return
        const list = (data.companies || []).map(c => ({ name: c.name, lc: c.name.toLowerCase() }))
        COMPANIES_CACHE = list
        setCompanies(list)
      })
      .catch(() => { if (!cancelled) setCompanies([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open])

  const totalCount   = selected.length + selectedCompanies.length
  const isRoleSelected    = id   => selected.some(s => s.taxonomyId === id)
  const isCompanySelected = name => selectedCompanies.includes(name)

  // Compute suggestions
  const q = query.trim().toLowerCase()

  // Idle: only show currently-selected items + a small slice of popular roles.
  // Searching: mix roles + companies that match, roles first.
  const matches = useMemo(() => {
    if (!q) {
      const selRoles = selected
        .map(s => ROLE_INDEX.find(r => r.id === s.taxonomyId))
        .filter(Boolean)
        .map(r => ({ kind: "role", id: r.id, title: r.title }))
      const selCos = selectedCompanies.map(name => ({ kind: "company", id: name, title: name }))
      if (selRoles.length + selCos.length > 0) return [...selRoles, ...selCos]
      // Nothing selected → surface a few popular roles so the dropdown isn't empty.
      return ROLE_INDEX.slice(0, MAX_RESULTS_PER_GROUP)
        .map(r => ({ kind: "role", id: r.id, title: r.title }))
    }
    const roleHits = ROLE_INDEX
      .filter(r => r.lc.includes(q))
      .slice(0, MAX_RESULTS_PER_GROUP)
      .map(r => ({ kind: "role", id: r.id, title: r.title }))
    const coHits = companies
      .filter(c => c.lc.includes(q))
      .slice(0, MAX_RESULTS_PER_GROUP)
      .map(c => ({ kind: "company", id: c.name, title: c.name }))
    return [...roleHits, ...coHits]
  }, [q, selected, selectedCompanies, companies])

  const handleRow = item => {
    if (item.kind === "role") onToggle({ taxonomyId: item.id, title: item.title })
    else onCompanyToggle?.(item.id)
  }

  const noResults = q && matches.length === 0 && !loading

  return (
    <div ref={ref} style={{ position: "relative", ...(wrapperStyle || {}) }}>
      <style>{`
        @keyframes rtf-fadeUp { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        .rtf-pop { animation: rtf-fadeUp 0.16s ease; }
        .rtf-input::placeholder { color: #a1a1aa; }
        .rtf-item { transition: background 0.1s; }
        .rtf-item:hover { background: #fafafa; }
        .rtf-item:hover .rtf-chip { background: #ffffff; }
        .rtf-chip-pill:hover { background: #ececec; }
      `}</style>

      {/* Trigger — custom or default */}
      {renderTrigger ? (
        renderTrigger({ open, toggle: () => setOpen(o => !o), count: totalCount })
      ) : (
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            height: 28, padding: "0 10px",
            background: totalCount > 0 ? C.accentBg : C.filterBtnBg,
            border: `1px solid ${totalCount > 0 ? C.accentBorder : C.filterBtnBorder}`,
            borderRadius: 7, fontSize: 12, fontWeight: totalCount > 0 ? 600 : 400,
            color: totalCount > 0 ? C.accentText : C.filterBtnText,
            fontFamily: FF, cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap",
          }}
        >
          Role
          {totalCount > 0 && (
            <span style={{ background: C.accentBadgeBg, borderRadius: 9999, padding: "0 6px", fontSize: 10, fontWeight: 700, lineHeight: "17px", color: C.accentText }}>
              {totalCount}
            </span>
          )}
          <ChevronDown size={10} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s", opacity: 0.6 }} />
        </button>
      )}

      {/* Dropdown */}
      {open && (
        <div
          className="rtf-pop"
          style={{
            position: "absolute",
            top: "calc(100% + 10px)",
            [dropdownAlign]: 0,
            width: 520,
            background: "#ffffff",
            border: "1px solid #f0f0f0",
            borderRadius: 14,
            boxShadow: "0 12px 40px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.03)",
            zIndex: 1000,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            maxHeight: 440,
            fontFamily: FF,
          }}
        >
          {/* Toolbar — only visible when there's something to clear */}
          {totalCount > 0 && !query && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "flex-end",
              padding: "10px 14px 4px",
              flexShrink: 0,
            }}>
              <button
                onClick={e => {
                  e.stopPropagation()
                  onClear?.()
                  onCompanyClear?.()
                }}
                style={{
                  fontSize: 11, fontWeight: 600, color: "#71717a",
                  background: "none", border: "none", cursor: "pointer",
                  fontFamily: FF, display: "inline-flex", alignItems: "center",
                  gap: 3,
                }}
              >
                <X size={10} strokeWidth={2.4} />Clear all
              </button>
            </div>
          )}

          {/* Results */}
          <div style={{ flex: 1, overflowY: "auto", padding: "6px 0" }}>
            {noResults ? (
              <div style={{ padding: "28px 16px", textAlign: "center", color: "#9a9a9a", fontSize: 13 }}>
                No matches for “{query}”.
              </div>
            ) : (
              matches.map(item => {
                const sel = item.kind === "role"
                  ? isRoleSelected(item.id)
                  : isCompanySelected(item.id)
                return (
                  <Row
                    key={`${item.kind}:${item.id}`}
                    label={item.title}
                    kind={item.kind}
                    selected={sel}
                    onClick={() => handleRow(item)}
                  />
                )
              })
            )}
            {q && loading && (
              <div style={{ padding: "10px 16px", fontSize: 11.5, color: "#b4b4b8", fontFamily: FF }}>
                Loading companies…
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────
function Row({ label, kind, selected, onClick }) {
  const chipLabel = kind === "company" ? "Companies" : "Roles"
  return (
    <button
      type="button"
      onClick={onClick}
      className="rtf-item"
      style={{
        display: "flex", alignItems: "center", gap: 10,
        width: "100%", padding: "10px 14px",
        background: "none",
        border: "none", cursor: "pointer", textAlign: "left",
        fontFamily: FF,
      }}
    >
      <span style={{
        fontSize: 13.5, fontWeight: 500, color: "#18181b",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        flex: 1, minWidth: 0,
      }}>
        {label}
      </span>
      <span
        className="rtf-chip"
        style={{
          display: "inline-flex", alignItems: "center",
          height: 22, padding: "0 9px",
          background: "#f6f6f7",
          color: "#71717a",
          borderRadius: 9999,
          fontSize: 10.5, fontWeight: 600,
          letterSpacing: "0.02em",
          whiteSpace: "nowrap", flexShrink: 0,
          transition: "background 0.1s",
        }}
      >
        {chipLabel}
      </span>
      <span style={{
        width: 18, height: 18,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        color: selected ? "#18181b" : "transparent",
        flexShrink: 0,
      }}>
        <CheckCheck size={16} strokeWidth={2.4} />
      </span>
    </button>
  )
}
